import {addToast, PasswordField} from '@albert-io/atomic';
import React, {ChangeEvent, useState} from 'react';

import ResetPassword from 'client/components/ResetPassword/ResetPassword';
import {useResetPasswordStore} from 'client/components/ResetPassword/ResetPassword.store';
import {AppError} from 'lib/normalizeError';
import {
  useManagedUserSelfResetPasswordMutation,
  useResetPasswordWithOldPasswordMutation
} from '../ResetPassword/ResetPassword.queries.generated';

interface OldPasswordFieldProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

const OldPasswordField = ({onChange, ...rest}: OldPasswordFieldProps) => {
  const inputType = useResetPasswordStore((state) => state.inputType);
  return <PasswordField onChange={onChange} type={inputType} label='Current password' {...rest} />;
};

interface ChangePasswordProps {
  legend?: string;
  userId: string;
  checkOldPassword?: boolean;
  [key: string]: any;
}

/**
 * ChangePassword
 * ==============
 *
 * The ChangePassword component appears in:
 *   1) the Account Settings page (PasswordAndSettings.tsx)
 *   2) the landing page for users whose token requires them to
 *      update their password (ChangePasswordLandingPage.tsx)
 *
 * It returns an instance of a ResetPassword component but with
 * the addition of an "Old Password" field, plus the necessary
 * handlers for changes, successful saving, and errors.
 *
 * Props:
 *    checkOldPassword - whether to require the current password
 *        (defaults to true; false for managed users)
 *    legend - a string that will appear inside our
 *        <ResetPassword.Legend /> tags
 */
const ChangePassword = ({
  legend,
  userId,
  checkOldPassword = true,
  ...rest
}: ChangePasswordProps) => {
  const [oldPassword, setOldPassword] = useState('');
  const [oldPasswordError, setOldPasswordError] = useState('');

  const [resetPasswordWithOldPasswordMutation] = useResetPasswordWithOldPasswordMutation();
  const [managedUserSelfResetPasswordMutation] = useManagedUserSelfResetPasswordMutation();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOldPassword(e.target.value);
    setOldPasswordError('');
  };

  const handleError = (error: AppError) => {
    if (error.detail === 'Old password is incorrect.') {
      setOldPasswordError('Incorrect password. Please try again.');
    } else {
      addToast({
        title: 'Oops! Looks like something went wrong',
        color: 'negative',
        message: 'There was a problem with your request. If the problem persists, contact us.'
      });
    }
  };

  const handleSubmit = async (password: string) => {
    if (!checkOldPassword) {
      const {data} = await managedUserSelfResetPasswordMutation({
        variables: {
          input: {newPassword: password}
        }
      });

      const result = data?.managedUserSelfResetPassword;
      if (result?.error) {
        throw result.error;
      }
      return;
    }

    const {data} = await resetPasswordWithOldPasswordMutation({
      variables: {
        input: {
          oldPassword: oldPassword,
          newPassword: password
        }
      },
      fetchPolicy: 'no-cache'
    });

    const result = data?.resetPasswordWithOldPassword;
    if (result?.error) {
      throw result.error;
    }
  };

  return (
    <ResetPassword
      customDisableSubmitCondition={checkOldPassword && oldPassword === ''}
      onError={handleError}
      onSubmit={handleSubmit}
      {...rest}
    >
      {legend && <ResetPassword.Legend>{legend}</ResetPassword.Legend>}
      <ResetPassword.Form>
        {checkOldPassword && (
          <OldPasswordField
            borders
            className='mb-x2'
            error={!!oldPasswordError}
            message={oldPasswordError}
            onChange={handleChange}
            value={oldPassword}
          />
        )}
        <ResetPassword.NewPasswordField />
        <ResetPassword.ConfirmPasswordField />
        <ResetPassword.ShowPasswordToggle />
      </ResetPassword.Form>
    </ResetPassword>
  );
};

export default ChangePassword;
